/* NAV adófolyószámla (M2M Adózó API) — beállítás-panel.
 *
 * ⚠⚠ EZ NEM AZ ONLINE SZÁMLA. Az a bejövő számlák letöltése (Szinkronizálás →
 * „NAV Online Számla"); ez az adófolyószámla-tükör: tartozás/túlfizetés
 * adónemenként, NAV-bankszámlaszámmal. Két külön NAV-rendszer, két külön
 * regisztráció. Részletek: wiki/M2M_API.md, wiki/nav-upo-integracio.md.
 *
 * Külön fájlban van, nem a misc.js-ben: az a fájl már 4300+ sor, és ez a panel
 * önálló, jól határolt felület.
 *
 * HÁROM DOLOG, AMI ITT SZÁNDÉKOS:
 *
 * 1. A titok-mezők WRITE-ONLY. A felület sosem kapja meg az értéket (a backend
 *    csak `is_set`-et ad), ezért az input mindig ÜRESEN indul, és az üresen
 *    hagyott mező azt jelenti: „ne változtass". Törlésre külön gomb van —
 *    különben egy mentés véletlenül kilőné az egész integrációt.
 * 2. A mentés ELŐTT kérünk step-up-ot (friss jelszót), nem a 403-ra reagálunk
 *    (`.claude/rules/munkamenet-auth.md` 8.). Indok: a NAV-válaszból származik az
 *    utalás célszámlája (`adonem_szamlaszam`), tehát egy ellopott munkamenettel
 *    átírt credential közvetve rossz számlára utalást okozhat.
 * 3. Az aláírás-variáns KIPRÓBÁLHATÓ mentés nélkül (`probe`). A pontos
 *    string-összefűzés [Ellenőrizendő] — a timestamp nem utazik a kérésben, tehát
 *    a NAV a saját idejét használja ismeretlen bontással. Egy félúton elmentett,
 *    rossz variáns a napi szinkront rontaná el.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Play, Trash2, Download, ShieldCheck } from 'lucide-react';
import { navUpoApi } from '../services/api';
import { downloadAuthedFile } from '../components/AuthedFile';
import { Btn, Card, Alert, Amount, Input, Select, Badge, Checkbox, Spinner,
         ConfirmDialog } from '../components/UI';
import { useToast } from '../components/Toast';
import { useStepUp } from '../contexts/StepUpContext';
import { useAuth } from '../contexts/AuthContext';

const FIELD_LABELS = {
  client_id:                 'Client ID',
  client_secret:             'Client secret',
  username:                  'ÜPO felhasználónév',
  password:                  'ÜPO jelszó',
  signature_key_first_part:  'Aláírókulcs — 1. fél',
  signature_key_second_part: 'Aláírókulcs — 2. fél (aktiválásból, nem kézzel)',
  activation_nonce:          'Aktiválási kód (a 2. kulcsfél kiváltásához)',
};

const RED = '#ef4444';

const fmtTs = (iso) => iso
  ? new Date(iso).toLocaleString('hu-HU', { timeZone: 'Europe/Budapest',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit' })
  : '—';

function Stat({ label, value }) {
  return (
    <div>
      <div style={{fontSize:'0.72rem',color:'var(--text-muted)',marginBottom:2}}>{label}</div>
      <div style={{fontSize:'0.95rem',fontWeight:600}}>{value}</div>
    </div>
  );
}

export default function NavUpoPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const { requireStepUp } = useStepUp();
  const { isAdmin } = useAuth();
  const [confirm, setConfirm] = useState(null);
  const [draft, setDraft] = useState({});          // csak a MOST beírt titkok
  const [probeResult, setProbeResult] = useState(null);
  const [onlyErrors, setOnlyErrors] = useState(false);

  const { data: state = {}, isLoading } = useQuery({
    queryKey: ['nav-upo-state'], queryFn: navUpoApi.state,
  });
  const { data: variantData = {} } = useQuery({
    queryKey: ['nav-upo-variants'], queryFn: navUpoApi.variants,
  });
  /* A NAV sandbox teszt-adózói. Éles környezetben a végpont `applicable:false`-ot
   * ad — ott a lista üres, mert az override élesben nem érvényesül, és egy
   * kiválasztható dropdown hamis képet adna arról, mit tud a rendszer. */
  const { data: taxpayerData = {} } = useQuery({
    queryKey: ['nav-upo-test-taxpayers'], queryFn: navUpoApi.testTaxpayers,
  });
  const taxpayers = taxpayerData.taxpayers || [];
  // Ha a MOST érvényes azonosító teszt-adózó, azt ki KELL írni: a felületen
  // egyébként úgy tűnne, mintha a saját cégünk adatait látnánk.
  const aktivTeszt = taxpayers.find(t => t.azonosito === state.effective_adoalany);
  const { data: log = {} } = useQuery({
    queryKey: ['nav-upo-call-log', onlyErrors],
    queryFn: () => navUpoApi.callLog({ limit: 50, only_errors: onlyErrors }),
  });

  const creds = state.credentials || {};
  const enabled = state.enabled === true;
  const environment = state.environment || 'test';
  const isTest = environment !== 'production';
  const variants = variantData.variants || [];
  const snap = state.snapshot;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['nav-upo-state'] });
    qc.invalidateQueries({ queryKey: ['nav-upo-call-log'] });
  };

  const saveMut = useMutation({
    mutationFn: (values) => navUpoApi.saveCredentials(values),
    onSuccess: (d) => {
      setDraft({});
      toast.success(d.written?.length
        ? `Mentve: ${d.written.map(f => FIELD_LABELS[f] || f).join(', ')}`
        : 'Nem volt mit menteni.');
      refresh();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'A mentés nem sikerült.'),
  });
  const clearMut = useMutation({
    mutationFn: (field) => navUpoApi.clearCredentials([field]),
    onSuccess: () => { toast.success('Törölve.'); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'A törlés nem sikerült.'),
  });
  // ⚠ Környezet-váltó NINCS többé itt (2026-09-04, M3): a sandbox/éles a cég-szintű
  // `companies.is_sandbox`, amit csak a PhenoFin-Admin konzol állít — „nincs félig
  // sandbox cég". A felület csak MUTATJA, melyikben vagyunk.
  // Teszt-környezetben váltható, MELYIK adóalanyt kérdezzük.
  const [adoalanyDraft, setAdoalanyDraft] = useState('');
  const adoalanyMut = useMutation({
    mutationFn: (v) => navUpoApi.setAdoalanyOverride(v),
    onSuccess: (d) => {
      toast.success(`Lekérdezett adóalany: ${d.effective_adoalany || '—'}`);
      refresh();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Nem sikerült.'),
  });
  const variantMut = useMutation({
    mutationFn: (v) => navUpoApi.setVariant(v),
    onSuccess: () => { toast.success('Aláírás-variáns mentve.'); refresh(); },
  });
  const probeMut = useMutation({
    mutationFn: (v) => navUpoApi.probe(v),
    onSuccess: (d) => { setProbeResult(d); refresh(); },
    onError: (e) => setProbeResult({ ok: false,
      error: e?.response?.data?.detail || e.message }),
  });

  // ⏱ Aktiválás: a nonce EGYSZER használatos és 72 órás. A backend a beváltott
  // kulcsfelet azonnal commitolja, még az aktiválás megkísérlése előtt.
  const [activateResult, setActivateResult] = useState(null);
  const activateMut = useMutation({
    mutationFn: () => navUpoApi.activate(),
    onSuccess: (d) => {
      setActivateResult(d);
      if (d.ok) toast.success('A regisztráció aktiválva.');
      refresh();
    },
    onError: (e) => setActivateResult({ ok: false,
      steps: [{ step: 'activate', error: e?.response?.data?.detail || e.message }] }),
  });

  // A titkok mentése step-up mögött — a művelet ELŐTT kérdezünk (lásd 2. pont).
  const onSave = async () => {
    const values = Object.fromEntries(
      Object.entries(draft).filter(([, v]) => (v || '').trim() !== ''));
    if (!Object.keys(values).length) { toast.info('Nincs kitöltött mező.'); return; }
    if (!await requireStepUp('A NAV-hozzáférés módosítása előtt ')) return;
    saveMut.mutate(values);
  };

  const onClear = (field) => setConfirm({
    title: 'Titok törlése', danger: true, confirmLabel: 'Törlés',
    message: `Biztosan törlöd a(z) „${FIELD_LABELS[field] || field}" mezőt? `
           + 'A NAV-lekérdezés enélkül nem fog működni, amíg újra be nem állítod.',
    action: async () => {
      if (!await requireStepUp('A NAV-hozzáférés módosítása előtt ')) return;
      clearMut.mutate(field);
    },
  });

  // A cégenkénti ki/be kapcsoló (2026-09-27). Alapértelmezés KI — kikapcsolva semmilyen
  // NAV ÜPO hívás nem megy; a letöltött adat és a hitelesítő adatok megmaradnak.
  const enabledMut = useMutation({
    mutationFn: (v) => navUpoApi.setEnabled(v),
    onSuccess: (d) => {
      toast.success(d.enabled ? 'A NAV ÜPO bekapcsolva.' : 'A NAV ÜPO kikapcsolva.');
      qc.invalidateQueries({ queryKey: ['nav-upo-state'] });
      qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message),
  });
  const onToggleEnabled = () => {
    if (!enabled) { enabledMut.mutate(true); return; }
    setConfirm({
      title: 'NAV ÜPO kikapcsolása', danger: true, confirmLabel: 'Kikapcsolás',
      message: 'Kikapcsolás után nem kérdezzük le a NAV-tól az adófolyószámlát, a hiányzó '
             + 'bevallásokat és a köztartozás-mentességet, és a Kezdőlapon sem jelezzük őket. '
             + 'A már letöltött adat és a hozzáférési adatok megmaradnak.',
      action: () => enabledMut.mutate(false),
    });
  };

  if (isLoading) return <Spinner/>;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a = confirm.action; setConfirm(null); a(); }}/>

      {/* ── Ki/be kapcsoló ── */}
      <Card title="NAV adófolyószámla-lekérdezés"
        subtitle="Bekapcsolva naponta lekérdezzük a NAV-tól az adófolyószámlát, a hiányzó bevallásokat és a köztartozás-mentességet.">
        <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
          <Badge variant={enabled ? 'success' : 'default'}>{enabled ? 'Bekapcsolva' : 'Kikapcsolva'}</Badge>
          <Btn size="sm" variant={enabled ? 'ghost' : 'primary'} loading={enabledMut.isPending}
            disabled={!isAdmin} onClick={onToggleEnabled}
            title={isAdmin ? undefined : 'Csak a cég adminisztrátora kapcsolhatja.'}>
            {enabled ? 'Kikapcsolás' : 'Bekapcsolás'}
          </Btn>
          {!isAdmin && (
            <span style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>
              Csak a cég adminisztrátora kapcsolhatja.
            </span>
          )}
        </div>
        {!enabled && (
          <Alert type="info" style={{marginTop:12}}>
            A NAV adófolyószámla-lekérdezés ki van kapcsolva: most semmilyen lekérdezés nem
            megy a NAV-hoz. A hozzáférési adatokat ettől függetlenül beállíthatod.
          </Alert>
        )}
      </Card>

      {/* ── Állapot ── */}
      <Card title="Állapot"
        subtitle="Az adófolyószámla-tükör legutolsó lekérdezése. Ez NEM az Online Számla (bejövő számlák) — az a Szinkronizálás → NAV Online Számla.">
        {!creds.complete && (
          <Alert type="warning">
            A hozzáférés még nincs teljesen beállítva
            {creds.missing_required?.length
              ? <> — hiányzik: <b>{creds.missing_required.map(f => FIELD_LABELS[f] || f).join(', ')}</b>.</>
              : '.'}
          </Alert>
        )}

        <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:12,flexWrap:'wrap'}}>
          <span style={{fontSize:'0.8rem',color:'var(--text-muted)'}}>Környezet:</span>
          <Badge variant={isTest ? 'warning' : 'danger'}>
            {isTest ? 'TESZT (m2m-dev)' : 'ÉLES (m2m)'}
          </Badge>
          <span style={{fontSize:'0.72rem',color:'var(--text-dim)'}}
            title="A teszt/éles környezet cég-szintű beállítás: minden integrációra (NAV, bankok) egyszerre érvényes.">
            A környezetet a rendszergazda állítja, cég-szinten.
          </span>
        </div>

        {/* ── Melyik adóalanyt kérdezzük (teszt-környezetben váltható) ──
            ⚠ Enélkül egy 0 Ft-os válasz értelmezhetetlen: a teszt-környezet
            anonimizált teszt-adózókkal dolgozik, a saját cégünkre nincs adat. */}
        {isTest && (
          <div style={{border:'1px dashed var(--border)',borderRadius:'var(--radius)',
            padding:'0.6rem 0.75rem',marginBottom:12}}>
            {/* A dropdown a MÉRT teszt-adózókat ajánlja ki (mind a 8-at éles
                hívással ellenőriztük — wiki/M2M_API.md 6.H). A szabad szöveges
                mező MEGMARAD mellette: a NAV bármikor bővítheti a listát, és egy
                zárt dropdown akkor kényszerítene kód-módosításra. */}
            <div style={{display:'flex',gap:8,alignItems:'end',flexWrap:'wrap'}}>
              {taxpayers.length > 0 && (
                <div style={{minWidth:330,flex:'1 1 330px'}}>
                  {/* ⚠ A `Select` `options`-t vár, nem childrent (components/UI.js). */}
                  <Select label="Teszt-adózó (NAV sandbox)"
                    value={adoalanyDraft}
                    onChange={e => { const v = e.target.value; setAdoalanyDraft(v);
                      if (v) adoalanyMut.mutate(v); }}
                    options={[
                      { value: '', label: '— válassz, vagy írd be kézzel —' },
                      ...taxpayers.map(t => ({
                        value: t.azonosito,
                        label: `${t.azonosito} · ${t.nev}`
                             + ` (${t.tipus}${t.adonem ? `, ${t.adonem} adónem` : ''})`,
                      })),
                    ]}/>
                </div>
              )}
              <Input label="…vagy adóalany-azonosító kézzel" value={adoalanyDraft}
                placeholder={state.effective_adoalany || 'a cég adószámából'}
                onChange={e => setAdoalanyDraft(e.target.value)}
                style={{maxWidth:190}}/>
              <Btn size="sm" variant="ghost" loading={adoalanyMut.isPending}
                onClick={() => adoalanyMut.mutate(adoalanyDraft)}>Beállítás</Btn>
              <Btn size="sm" variant="ghost" loading={adoalanyMut.isPending}
                onClick={() => { setAdoalanyDraft(''); adoalanyMut.mutate(''); }}>
                Vissza a cégre
              </Btn>
            </div>
            <div style={{display:'flex',gap:10,alignItems:'center',marginTop:8,
              flexWrap:'wrap'}}>
              <span style={{fontSize:'0.74rem',color:'var(--text-muted)'}}>
                most: <b style={{fontFamily:'var(--mono)'}}>
                  {state.effective_adoalany || '—'}</b>
                {aktivTeszt && <> — <b>{aktivTeszt.nev}</b></>}
              </span>
              {aktivTeszt && (
                <Badge variant="warning">
                  a felület MÁS adózó adatait mutatja
                </Badge>
              )}
            </div>
            <p style={{fontSize:'0.72rem',color:'var(--text-dim)',margin:'6px 0 0'}}>
              A NAV teszt-környezete előre definiált, anonimizált teszt-adózókkal
              dolgozik, a saját cégünkre nincs benne adat (a lekérdezés sikeres, de
              0 Ft) — a rendszer képességeinek bemutatásához ezért kell átmenetileg
              teszt-adózót választani. ⚠ A 8 jegyű törzsszám a helyes forma — a
              11 jegyűt a NAV elutasítja. Élesben ez a mező nem érvényesül.
              A váltás után futtass egy szinkront (Szinkronizálás → NAV
              adófolyószámla); a korábbi adózó tételei megmaradnak, de a
              lekérdezéskor a NAV a most választott adózót adja.
            </p>
          </div>
        )}

        {snap ? (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:12}}>
            <Stat label="Tartozás" value={<Amount value={snap.tartozas} currency="HUF"/>}/>
            <Stat label="Túlfizetés" value={<Amount value={snap.tulfizetes} currency="HUF"/>}/>
            <Stat label="Könyveletlen" value={<Amount value={snap.konyveletlen_osszesen} currency="HUF"/>}/>
            <Stat label="Nyitott tételek" value={state.nyitott_tetelek ?? 0}/>
            <Stat label="Utolsó lekérdezés" value={fmtTs(snap.queried_at)}/>
          </div>
        ) : (
          <p style={{fontSize:'0.82rem',color:'var(--text-muted)',marginBottom:0}}>
            Még nem futott lekérdezés. A szinkron a Szinkronizálás ablakból indítható.
          </p>
        )}

        {state.eltunt_tetelek > 0 && (
          <Alert type="warning" style={{marginTop:10}}>
            <b>{state.eltunt_tetelek}</b> tétel kikerült a NAV válaszából (eltűnt). A hozzájuk
            tartozó bizonylatokat NEM töröltük — felülvizsgálatot igényelnek.
          </Alert>
        )}
        {state.last_error && (
          <Alert type="danger" style={{marginTop:10}}>
            Utolsó hiba ({fmtTs(state.last_error.at)}, {state.last_error.endpoint}):{' '}
            {state.last_error.result_code || ''} {state.last_error.error}
          </Alert>
        )}
      </Card>

      {/* ── Adónem-bontás ── */}
      {(state.adonemek || []).length > 0 && (
        <Card title="Adónemenkénti egyenleg"
          subtitle={state.adonem_snapshot_id && snap && state.adonem_snapshot_id !== snap.id
            ? 'A bontás a legutolsó olyan lekérdezésből való, ami tételes adatot is hozott — a változatlan napi szinkron szándékosan nem írja újra.'
            : 'A NAV adónem-szintű egyenlege. Az irányt a NAV minősítése adja, nem az összeg előjele.'}>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',fontSize:'0.8rem',borderCollapse:'collapse'}}>
              <thead><tr style={{textAlign:'left',color:'var(--text-muted)'}}>
                <th style={{padding:'6px 8px'}}>Kód</th>
                <th style={{padding:'6px 8px'}}>Adónem</th>
                <th style={{padding:'6px 8px',textAlign:'right'}}>Egyenleg</th>
                <th style={{padding:'6px 8px'}}>Minősítés</th>
                <th style={{padding:'6px 8px'}}>NAV-számlaszám</th>
              </tr></thead>
              <tbody>
                {state.adonemek.map(a => (
                  <tr key={a.adonem_kod} style={{borderTop:'1px solid var(--border)'}}>
                    <td style={{padding:'6px 8px',fontFamily:'monospace'}}>{a.adonem_kod}</td>
                    <td style={{padding:'6px 8px'}}>{a.megnevezes || '—'}</td>
                    <td style={{padding:'6px 8px',textAlign:'right'}}>
                      <Amount value={a.egyenleg} currency="HUF"/></td>
                    <td style={{padding:'6px 8px'}}>
                      {a.minosites === 'TULFIZETES' ? <Badge variant="success">túlfizetés</Badge>
                        : a.minosites === 'TARTOZAS' ? <Badge variant="danger">tartozás</Badge>
                        : <Badge>{a.minosites || '—'}</Badge>}
                    </td>
                    <td style={{padding:'6px 8px',fontFamily:'monospace',fontSize:'0.76rem'}}>
                      {a.szamlaszam || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Hozzáférés (write-only titkok) ── */}
      <Card title="Hozzáférés"
        subtitle="A titkokat mentés után nem lehet visszaolvasni (a NAV működési elvárása). Az üresen hagyott mező változatlanul marad — törlésre külön gomb van.">
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {(creds.fields || []).map(f => (
            <div key={f.field} style={{display:'grid',
              gridTemplateColumns:'1fr auto auto',gap:8,alignItems:'end'}}>
              <Input label={<>{FIELD_LABELS[f.field] || f.field}
                  {f.required && <span style={{color:RED}}> *</span>}</>}
                type="password" autoComplete="new-password"
                placeholder={f.is_set
                  ? '•••••••• (beállítva — hagyd üresen, ha nem változik)'
                  : 'nincs beállítva'}
                value={draft[f.field] || ''}
                onChange={e => setDraft(d => ({ ...d, [f.field]: e.target.value }))}/>
              <div style={{fontSize:'0.72rem',color:'var(--text-muted)',
                paddingBottom:8,whiteSpace:'nowrap'}}>
                {f.is_set ? <>✓ beállítva<br/>{fmtTs(f.updated_at)}</> : '—'}
              </div>
              <Btn size="sm" variant="ghost" icon={Trash2} disabled={!f.is_set}
                onClick={()=>onClear(f.field)} style={{marginBottom:6}}/>
            </div>
          ))}
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:12}}>
          <Btn variant="ghost" onClick={()=>setDraft({})}
            disabled={!Object.keys(draft).length}>Mégse</Btn>
          <Btn onClick={onSave} loading={saveMut.isPending}
            disabled={!Object.keys(draft).length}>Titkok mentése</Btn>
        </div>
        <p style={{fontSize:'0.72rem',color:'var(--text-dim)',marginTop:8,marginBottom:0}}>
          A mentés friss jelszó-megerősítést kér. Az aláírókulcs két félből áll: az elsőt a
          regisztrációkor kapod, a másodikat a nonce-beváltás adja.
        </p>
      </Card>

      {/* ── Aktiválás ── */}
      {(() => {
        const nonce = (creds.fields || []).find(f => f.field === 'activation_nonce');
        const second = (creds.fields || []).find(
          f => f.field === 'signature_key_second_part');
        // Csak akkor mutatjuk, ha van dolgunk: van beváltható kód, vagy még
        // hiányzik a második kulcsfél (tehát az aktiválás nem futott le).
        if (!nonce?.is_set && second?.is_set) return null;
        return (
          <Card title="Regisztráció aktiválása"
            subtitle="Az aktiválást a NAV szabálya szerint a kliensprogramnak kell elvégeznie: beváltja az aktiválási kódot (ebből jön az aláírókulcs második fele), majd aktiválja a regisztrációt.">
            <Alert type={nonce?.is_set ? 'warning' : 'info'}>
              {nonce?.is_set ? (
                <>⏱ <b>Az aktiválási kód egyszer használatos, és a regisztrációtól
                számított 72 órán belül be kell váltani</b> — utána a teljes
                végfelhasználói regisztrációt meg kell ismételni az ÜPO-n (nem elég
                új kódot kérni).</>
              ) : (
                <>Nincs eltárolt aktiválási kód. Ha a regisztráció még nincs
                aktiválva, add meg a kódot a „Hozzáférés" szakaszban, majd térj
                vissza ide.</>
              )}
            </Alert>
            <div style={{display:'flex',justifyContent:'flex-end',marginTop:10}}>
              <Btn icon={ShieldCheck} loading={activateMut.isPending}
                disabled={!enabled || (!nonce?.is_set && !second?.is_set)}
                onClick={async () => {
                  if (!await requireStepUp('A NAV-regisztráció aktiválása előtt ')) return;
                  setActivateResult(null);
                  activateMut.mutate();
                }}>
                Aktiválás indítása
              </Btn>
            </div>
            {activateResult && (
              <Alert type={activateResult.ok ? 'success' : 'danger'}
                style={{marginTop:10}}>
                {(activateResult.steps || []).map((s, i) => (
                  <div key={i}>
                    <b>{s.step === 'redeem_nonce' ? 'Kód beváltása' : 'Aktiválás'}:</b>{' '}
                    {s.skipped ? `kihagyva — ${s.reason}`
                      : s.ok ? `sikeres${s.already_activated ? ' (már aktivált volt)' : ''}`
                      : `nem sikerült — ${s.result_code || ''} ${s.error || ''}`}
                    {s.saved && ' · a kulcs második fele elmentve'}
                    {s.retry_with_other_variant &&
                      ' · próbáld másik aláírás-variánssal (a kód már be van váltva, nem veszett el)'}
                  </div>
                ))}
              </Alert>
            )}
          </Card>
        );
      })()}

      {/* ── Aláírás-variáns ── */}
      <Card title="Aláírás-variáns"
        subtitle="A NAV nem dokumentálja, milyen időbélyeg-bontással ellenőrzi az aláírást (a timestamp nem utazik a kérésben). A variánsok kipróbálhatók mentés nélkül; a működőt utána rögzítsd.">
        <div style={{display:'flex',gap:8,alignItems:'end',flexWrap:'wrap'}}>
          <Select label="Variáns" value={state.signature_variant || 'V1'}
            onChange={e => variantMut.mutate(e.target.value)} style={{minWidth:340}}
            options={variants.map(v => ({
              value: v.key,
              label: `${v.key} — ${v.ts} / ${v.encoding} / ${v.key_order}`
                   + (v.note ? ` · ${v.note}` : ''),
            }))}/>
          <Btn size="sm" variant="ghost" icon={Play} loading={probeMut.isPending}
            disabled={!enabled || !creds.complete}
            onClick={()=>{ setProbeResult(null); probeMut.mutate(undefined); }}>
            Kipróbálás (mentés nélkül)
          </Btn>
        </div>
        {probeResult && (
          <Alert type={probeResult.ok ? 'success' : 'danger'} style={{marginTop:10}}>
            {probeResult.ok
              ? <>A(z) <b>{probeResult.variant}</b> variánssal a NAV elfogadta a hívást
                  ({probeResult.result_code}, {probeResult.duration_ms} ms).</>
              : <>Nem sikerült{probeResult.variant ? <> ({probeResult.variant})</> : null}:{' '}
                  {probeResult.result_code || ''} {probeResult.error}
                  {probeResult.result_code === 'ERVENYTELEN_ALAIRAS'
                    ? ' — próbáld a következő variánst.' : ''}</>}
          </Alert>
        )}
      </Card>

      {/* ── Hívásnapló ── */}
      <Card title="Hívásnapló"
        subtitle="Minden NAV-hívás naplózva, 365 napig. A napló nem tartalmaz aláírást, tokent és kulcsot — a NAV működési elvárása szerint letölthető."
        actions={
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <Checkbox label="Csak hibák" checked={onlyErrors}
              onChange={e=>setOnlyErrors(e.target.checked)}/>
            <Btn size="sm" variant="ghost" icon={Download}
              onClick={()=>downloadAuthedFile(navUpoApi.callLogCsvUrl(onlyErrors), 'nav-upo-hivasnaplo.csv')}>
              CSV letöltés
            </Btn>
          </div>}>
        {(log.rows || []).length === 0 ? (
          <p style={{fontSize:'0.82rem',color:'var(--text-muted)',marginBottom:0}}>
            {onlyErrors ? 'Nincs hibás hívás.' : 'Még nem volt NAV-hívás.'}
          </p>
        ) : (
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',fontSize:'0.76rem',borderCollapse:'collapse'}}>
              <thead><tr style={{textAlign:'left',color:'var(--text-muted)'}}>
                <th style={{padding:'6px 8px'}}>Időpont</th>
                <th style={{padding:'6px 8px'}}>Végpont</th>
                <th style={{padding:'6px 8px'}}>Környezet</th>
                <th style={{padding:'6px 8px'}}>HTTP</th>
                <th style={{padding:'6px 8px'}}>resultCode</th>
                <th style={{padding:'6px 8px',textAlign:'right'}}>ms</th>
                <th style={{padding:'6px 8px'}}>Hiba</th>
              </tr></thead>
              <tbody>
                {log.rows.map(r => (
                  <tr key={r.id} style={{borderTop:'1px solid var(--border)',
                    background: r.ok ? 'transparent' : 'rgba(239,68,68,0.06)'}}>
                    <td style={{padding:'6px 8px',whiteSpace:'nowrap'}}>{fmtTs(r.called_at)}</td>
                    <td style={{padding:'6px 8px'}}>{r.endpoint}</td>
                    <td style={{padding:'6px 8px'}}>{r.environment}</td>
                    <td style={{padding:'6px 8px'}}>{r.http_status ?? '—'}</td>
                    <td style={{padding:'6px 8px'}}>
                      {r.ok ? <Badge variant="success">{r.result_code || 'OK'}</Badge>
                            : <Badge variant="danger">{r.result_code || 'hiba'}</Badge>}
                    </td>
                    <td style={{padding:'6px 8px',textAlign:'right'}}>{r.duration_ms ?? '—'}</td>
                    <td style={{padding:'6px 8px',color:'var(--text-muted)',maxWidth:280,
                      overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}
                      title={r.error || ''}>{r.error || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {log.total > (log.rows || []).length && (
              <p style={{fontSize:'0.72rem',color:'var(--text-dim)',marginTop:8,marginBottom:0}}>
                {log.rows.length} / {log.total} sor látszik. A teljes napló a CSV-ben.
              </p>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
